import { MultiselectAutosuggest } from '@seek/multiselect-autosuggest';
import { Box, Column, Columns, IconSearch, Text } from 'braid-design-system';
import { type ComponentProps } from 'react';

import * as styles from './CompanyAutosuggest.css';

export type CompanySuggestion = {
  id: string;
  name: string;
  icon?: string;
  organisationId?: string;
  published?: boolean;
  slug?: string;
};

type RenderSuggestions = ComponentProps<
  typeof MultiselectAutosuggest
>['renderSuggestions'];

type RenderSelectedItems = Exclude<
  ComponentProps<typeof MultiselectAutosuggest>['renderSelectedItems'],
  undefined
>;

const CompanyLogo = ({ src }: { src?: string }) => {
  if (!src) {
    return <Box className={styles.companyLogo} />;
  }

  return (
    <Box
      className={styles.companyLogoWithBackground}
      style={{
        backgroundImage: `url(${src})`,
      }}
    />
  );
};

const SuggestedCompany = ({
  icon,
  name,
  textSize,
}: {
  name: string;
  icon?: string;
  textSize?: 'small' | 'standard';
}) => {
  return (
    <Columns space="xsmall" alignY="center" align="center">
      <Column width="content">
        <CompanyLogo src={icon} />
      </Column>
      <Column>
        <Text size={textSize}>{name}</Text>
      </Column>
    </Columns>
  );
};

const defaultRenderSelectedItems: RenderSelectedItems = () => (
  <Text size="small">
    <IconSearch />
  </Text>
);

export interface CompanyAutosuggestProps {
  clearLabel: string;
  id: string;
  onClickSuggestionItem: (suggestion: CompanySuggestion) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  suggestions: CompanySuggestion[];
  'aria-label'?: string;
  description?: string;
  label?: string;
  message?: string;
  onFocus?: () => void;
  onItemsSelected?: (items: CompanySuggestion[]) => void;
  placeholder?: string;
  renderCustomSuggestion?: (
    suggestion: CompanySuggestion,
    props: {
      getItemProps: Parameters<RenderSuggestions>[0]['getItemProps'];
      isHighlighted: boolean;
      itemIndex: number;
    },
  ) => React.ReactNode;
  selectedItems?: CompanySuggestion[];
  textSize?: 'small' | 'standard';
  tone?: 'critical';
}

export const CompanyAutosuggest = ({
  id,
  label = '',
  'aria-label': ariaLabel,
  description,
  searchText,
  setSearchText,
  placeholder,
  onFocus,
  onClickSuggestionItem,
  onItemsSelected = () => {},
  suggestions,
  selectedItems = [],
  tone,
  message,
  clearLabel,
  textSize,
  renderCustomSuggestion,
}: CompanyAutosuggestProps) => {
  const renderSuggestions: RenderSuggestions = ({
    getItemProps,
    highlightedIndex,
  }) => {
    return (
      <>
        {suggestions.map((suggestion, itemIndex) => {
          const isHighlighted = highlightedIndex === itemIndex;

          let content;

          if (renderCustomSuggestion) {
            content = renderCustomSuggestion(suggestion, {
              isHighlighted,
              getItemProps,
              itemIndex,
            });
          }

          // If custom rendering returns undefined, use default suggestion rendering
          if (content === undefined) {
            content = (
              <SuggestedCompany
                icon={suggestion.icon}
                name={suggestion.name}
                textSize={textSize}
              />
            );
          }

          return (
            <Box
              key={`suggestion-item-${suggestion.id}-${suggestion.organisationId || suggestion.slug || 'no-org-slug'}`}
              component="li"
              background={isHighlighted ? 'formAccentSoftHover' : undefined}
              cursor="pointer"
              padding="small"
              {...getItemProps({
                item: suggestion,
                name: suggestion.name,
                index: itemIndex,
              })}
            >
              {content}
            </Box>
          );
        })}
      </>
    );
  };

  return (
    <MultiselectAutosuggest
      id={id}
      label={label}
      aria-label={ariaLabel}
      description={description}
      searchText={searchText}
      setSearchText={setSearchText}
      placeholder={placeholder}
      onFocus={onFocus}
      onClickSuggestionItem={onClickSuggestionItem}
      onItemsSelected={onItemsSelected}
      selectedItems={selectedItems}
      renderSelectedItems={defaultRenderSelectedItems}
      tone={tone}
      message={message}
      clearLabel={clearLabel}
      suggestions={suggestions}
      renderSuggestions={renderSuggestions}
    />
  );
};
